import type {
  DashboardExperience,
  EmbeddingContext,
  EmbeddingEvents,
  FrameOptions,
} from 'amazon-quicksight-embedding-sdk';
import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk';
import { useCallback, useRef, useState } from 'react';

import { Logger } from '~/lib/logger';

const logger = new Logger('useQuickSightEmbed');

const QS_CONTENT_LOAD_TIMEOUT_MS = 15_000;

// Shared QuickSight embedding context — reused across embeds, recreated on session expiry or retry.
let embeddingContext: EmbeddingContext | null = null;

/**
 * Removes the previous context's control frame iframe from <body>.
 * The SDK has no public API to clean these up, so we remove them manually
 * before creating a new context to prevent stale iframes accumulating in the DOM.
 */
const removeOrphanedControlFrames = () => {
  const controlFrames = document.querySelectorAll('body > iframe.quicksight-embedding-iframe[name$="-CONTROL"]');
  controlFrames.forEach((iframe) => iframe.remove());
};

const getEmbeddingContext = async (): Promise<EmbeddingContext> => {
  if (!embeddingContext) {
    removeOrphanedControlFrames();
    embeddingContext = await createEmbeddingContext();
  }
  return embeddingContext;
};

/**
 * Manages QuickSight dashboard embedding lifecycle.
 * Uses SDK events to coordinate async operations and show custom loading state.
 * @returns containerRef, embed, navigateTo, isEmbedded, isLoading, error
 */
export const useQuickSightEmbed = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<DashboardExperience | null>(null);
  const embedStartTimeRef = useRef<number | null>(null);

  const [isEmbedded, setIsEmbedded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Resolvers for SDK event-based promises
  const contentLoadResolverRef = useRef<{
    resolve: () => void;
    reject: (err: Error) => void;
  } | null>(null);
  const contentLoadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Returns a promise that resolves when CONTENT_LOADED fires
  const waitForContentLoaded = useCallback((startTime: number) => {
    return new Promise<void>((resolve, reject) => {
      if (contentLoadTimeoutRef.current) {
        clearTimeout(contentLoadTimeoutRef.current);
      }

      contentLoadTimeoutRef.current = setTimeout(() => {
        const elapsed = Math.round(performance.now() - startTime);
        logger.error(`Dashboard embed timed out after ${elapsed}ms waiting for CONTENT_LOADED event`);
        contentLoadResolverRef.current = null;
        reject(new Error('Dashboard timed out and failed to load.'));
      }, QS_CONTENT_LOAD_TIMEOUT_MS);

      contentLoadResolverRef.current = {
        resolve: () => {
          if (contentLoadTimeoutRef.current) {
            clearTimeout(contentLoadTimeoutRef.current);
          }
          contentLoadResolverRef.current = null;
          resolve();
        },
        reject: (err: Error) => {
          if (contentLoadTimeoutRef.current) {
            clearTimeout(contentLoadTimeoutRef.current);
          }
          contentLoadResolverRef.current = null;
          reject(err);
        },
      };
    });
  }, []);

  // Routes SDK events to resolve pending promises or surface errors post-embed
  const handleMessage = useCallback((event: EmbeddingEvents) => {
    const elapsed = embedStartTimeRef.current
      ? `${Math.round(performance.now() - embedStartTimeRef.current)}ms`
      : 'unknown';

    logger.info(`onMessage event: ${event.eventName} at ${elapsed}`, event);

    if (event.eventName === 'CONTENT_LOADED') {
      contentLoadResolverRef.current?.resolve();
    } else if (event.eventName === 'ERROR_OCCURRED') {
      logger.error('SDK ERROR_OCCURRED event:', JSON.stringify(event));
      const error = new Error('Dashboard failed to load');
      setError(error);
      contentLoadResolverRef.current?.reject(error);
    }
  }, []);

  // Logs frame lifecycle events (FRAME_STARTED, FRAME_MOUNTED, FRAME_LOADED)
  const handleFrameChange = useCallback<NonNullable<FrameOptions['onChange']>>((event) => {
    const elapsed = embedStartTimeRef.current
      ? `${Math.round(performance.now() - embedStartTimeRef.current)}ms`
      : 'unknown';

    logger.info(`onChange (frame) event: ${event.eventName} at ${elapsed}`, event);
  }, []);

  // Embeds dashboard iframe. Waits for CONTENT_LOADED before resolving.
  const embed = useCallback(
    async (url: string): Promise<void> => {
      if (!containerRef.current) {
        throw new Error('Container ref not available');
      }

      // Prevent duplicate embeds
      if (frameRef.current) {
        return;
      }

      const startTime = performance.now();
      embedStartTimeRef.current = startTime;

      setIsLoading(true);
      setError(null);

      try {
        const context = await getEmbeddingContext();
        containerRef.current.innerHTML = '';

        const contentLoadedPromise = waitForContentLoaded(startTime);

        const frame = await context.embedDashboard(
          {
            url,
            container: containerRef.current,
            height: '100%',
            width: '100%',
            resizeHeightOnSizeChangedEvent: true,
            onChange: handleFrameChange,
          },
          {
            toolbarOptions: { export: true, undoRedo: false, reset: true },
            attributionOptions: { overlayContent: false },
            onMessage: handleMessage,
          },
        );

        logger.info(`embedDashboard() resolved at ${Math.round(performance.now() - startTime)}ms`);
        frameRef.current = frame;
        await contentLoadedPromise;
        logger.info(`CONTENT_LOADED received at ${Math.round(performance.now() - startTime)}ms — embed complete`);
        setIsEmbedded(true);
      } catch (err) {
        const elapsed = Math.round(performance.now() - startTime);
        logger.error(`embed() failed at ${elapsed}ms:`, err);
        frameRef.current = null;
        const caughtError = err instanceof Error ? err : new Error('Failed to embed dashboard');
        setError(caughtError);
        throw caughtError;
      } finally {
        setIsLoading(false);
      }
    },
    [handleMessage, handleFrameChange, waitForContentLoaded],
  );

  // Destroys the SDK context, clears the container, and resets all state.
  const reset = useCallback(() => {
    if (contentLoadTimeoutRef.current) {
      clearTimeout(contentLoadTimeoutRef.current);
      contentLoadTimeoutRef.current = null;
    }

    contentLoadResolverRef.current = null;
    frameRef.current = null;
    embeddingContext = null;

    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }

    removeOrphanedControlFrames();
    setIsEmbedded(false);
    setIsLoading(false);
    setError(null);
  }, []);

  // Navigates to a different dashboard within the existing iframe
  const navigateTo = useCallback(async (dashboardId: string): Promise<void> => {
    if (!frameRef.current) {
      throw new Error('Dashboard not embedded yet');
    }

    await frameRef.current.navigateToDashboard(dashboardId);
  }, []);

  return {
    containerRef,
    embed,
    navigateTo,
    reset,
    isEmbedded,
    isLoading,
    error,
  };
};
